/**
 * 申請の右パネル(承認待ち・申請一覧)。申請の概要 + 承認ルート + 「全画面で開く」。
 * 承認 / 却下は上の帯(スクロールしても見える)。一覧の行をそのまま受け取る(申請 1 件の API は
 * 詳細ページが使う。パネルは行の情報で足りる)。
 */
import { Alert, Space, Tag } from "antd";

import type { WfRequest } from "../../lib/api";
import { jst } from "../../lib/format";
import { KeyValueList, PanelSection, SidePanel, Steps } from "../../ui";
import { kindLabel } from "./constants";
import { ApproveReject, DeleteClosed, SlaTag, Withdraw } from "./wfActions";
import { employeeLink, requestStatusTag } from "./wfBits";
import { approvalRoute } from "./wfRoute";

export function RequestPanel({ req, id, onClose }: { req: WfRequest | undefined; id: number; onClose: () => void }) {
  if (!req) {
    return (
      <SidePanel title={`申請 #${id}`} onClose={onClose} fullHref={`/hr/wf-requests/${id}`}>
        <Alert type="info" showIcon message="この一覧には無い申請" description="ビューや条件を変えて開き直すか、全画面で開く。" />
      </SidePanel>
    );
  }
  return (
    <SidePanel
      title={`#${req.id} ${kindLabel(req.kind)}`}
      ariaLabel={`申請 #${req.id} の詳細`}
      status={requestStatusTag(req)}
      sub={
        <Space size={6} wrap>
          <span>{employeeLink(req.employee_id, req.employee_name, req.employee_status)}</span>
          <SlaTag sla={req.sla} />
        </Space>
      }
      fullHref={`/hr/wf-requests/${req.id}`}
      barActions={
        <>
          <ApproveReject req={req} />
          <Withdraw key={req.id} req={req} onDone={onClose} />
          <DeleteClosed req={req} onDone={onClose} />
        </>
      }
      onClose={onClose}
    >
      <PanelSection title="申請の概要">
        <KeyValueList
          narrow
          items={[
            { label: "対象者", value: employeeLink(req.employee_id, req.employee_name, req.employee_status) },
            { label: "テンプレート", value: req.template_name },
            { label: "日付", value: req.start_date },
            { label: "SaaS", value: req.saas.length ? req.saas.join(" / ") : "" },
            { label: "工程", value: req.steps.length ? req.steps.join(" / ") : "" },
            { label: "申請者", value: req.requested_by, always: true },
            { label: "申請日時", value: jst(req.created_at) },
            { label: "承認", value: <Space size={4} wrap><span>{req.approvals}</span>{req.approved_by.map((a) => <Tag key={a}>{a}</Tag>)}</Space>, always: true },
            { label: "承認者", value: req.approver ? `${req.approver.label}(${req.approver.names.length} 人)` : "" },
            { label: "タスク", value: req.task_total ? `${req.task_done} / ${req.task_total}` : "" },
            { label: "却下の理由", value: req.status === "rejected" ? req.reason : "", wide: true },
            { label: "取り下げの理由", value: req.status === "withdrawn" ? req.reason : "", wide: true },
          ]}
        />
      </PanelSection>
      <PanelSection title="承認ルート">
        <Steps items={approvalRoute(req)} />
      </PanelSection>
    </SidePanel>
  );
}
